import { inject, injectable } from 'inversify';

import { TYPES } from '@/engine/ioc';

import type {
  AlgorithmExtension,
  ExtensionInfo,
  NamedValue,
  OnChangedCallbackT,
  OnChangedForCallbackT,
  OnFailedCallback,
  AncestryInfo,
  ValueConstructor,
  ValuesCollection,
  VertexInfo
} from '@/engine/DataFlow/DataFlow';
import type { IValue } from '@/engine/DataFlow/Graph';
import { DFVDExporter } from '../Serialization/DFVDExporter';
import { MCSDExporter } from '../Serialization/MCSDExporter';
import { IdentifiersListTuner } from './Tuners/IdentifiersListTuner';
import * as DT from './DataTypes';
import { DataFlowJsonIO } from '../Serialization/DataFlowJsonIO';
import type { ICasePersistent, ICaseState } from '@/engine/app/CasePersistance/CasePersistenceHub';
import type { Type } from '@/engine/DataFlow/Type';
import type { CallbackSubscription } from '../Utilities/CallbackManager';

import { NumberTuner } from './Tuners/NumberTuner';
import { BooleanTuner } from './Tuners/BooleanTuner';
import { DataFlowWithSnapshots } from './DataFlowWithSnapshots';
import { SynchronizationContext } from '../utils/AsyncHelpers';
import type { INotificationsCenter } from '../app/interfaces';

export interface StateMetadata {
  dataFlow: Array<VertexInfo>;
  dataFlowExtensions: Array<ExtensionInfo>;
  approvedNames: Array<string>;
}

@injectable()
export class PlanningAlgorithm implements ICasePersistent {
  private readonly _notificationsCenter: INotificationsCenter;

  private readonly _dataFlow = new DataFlowWithSnapshots();

  private readonly _synchronizationContext = new SynchronizationContext();

  private _approvedNames = new Set<string>();

  public constructor(@inject(TYPES.NotificationsCenter) notificationsCenter: INotificationsCenter) {
    this._notificationsCenter = notificationsCenter;
    (window as unknown as { algo: PlanningAlgorithm }).algo = this;
  }

  public initAlgorithmAsync(algorithmExtension: AlgorithmExtension): Promise<void> {
    return this._synchronizationContext.runAsyncTask(() => this._dataFlow.extendAsync(algorithmExtension));
  }

  public clearSnapshots(): Promise<void> {
    return this._synchronizationContext.runAsyncTask(() => this._dataFlow.invalidateSnapshots());
  }

  public switchAsync(
    stateIdFrom: string,
    stateIdTo: string,
    values: ValuesCollection,
    approvedNames: Array<string>,
    shouldNotify = true,
    shouldMemoise = false
  ): Promise<void> {
    return this._synchronizationContext.runAsyncTask(async () => {
      await this._dataFlow.switchAsync(stateIdFrom, stateIdTo, values, shouldNotify, shouldMemoise);
      this._approvedNames = new Set(approvedNames);
    });
  }

  public extendAlgorithmAsync(
    algorithmExtension: AlgorithmExtension,
    extensionDescriptor: string,
    shouldNotify = false
  ): Promise<void> {
    return this._synchronizationContext.runAsyncTask(() =>
      this._dataFlow.extendAsync(algorithmExtension, extensionDescriptor, shouldNotify)
    );
  }

  public dropAlgorithmExtensionAsync(extensionDescriptor: string): Promise<void> {
    return this._synchronizationContext.runAsyncTask(() => this._dataFlow.dropExtensionAsync(extensionDescriptor));
  }

  public async setValuesAsync(values: ValuesCollection): Promise<void> {
    return this._synchronizationContext.runAsyncTask(() => this._dataFlow.setValuesAsync(this._addTuneFlags(values)));
  }

  public setValueAsync(name: string, value: IValue | undefined): Promise<void> {
    return this.setValuesAsync([[name, value]]);
  }

  public setPrimitiveAsync(name: string, value: unknown): Promise<void> {
    if (typeof value == 'boolean') {
      return this.setValueAsync(name, new DT.BooleanT(value));
    } else if (typeof value == 'string') {
      return this.setValueAsync(name, new DT.StringT(value));
    } else if (typeof value == 'number') {
      return this.setValueAsync(name, new DT.NumberT(value));
    } else {
      throw new Error('Unknown primitive type: [' + typeof value + ']');
    }
  }

  public async areTuneValuesDefaultAsync(names: Array<string>, approvedMeansDefault = true): Promise<boolean> {
    for (const name of names) {
      const value = await this.getValueStrictAsync(name + '.flag', DT.TuneFlag);
      if (approvedMeansDefault) {
        if (value.value == DT.ETuneFlag.USER) {
          return false;
        }
      } else {
        if (value.value != DT.ETuneFlag.DEFAULT) {
          return false;
        }
      }
    }
    return true;
  }

  public setTuneValuesAsync(values: ValuesCollection): Promise<void> {
    const tuneValues: ValuesCollection = [];
    values.forEach((valueToUndo) => {
      tuneValues.push([valueToUndo[0] + '.user', valueToUndo[1]]);
      tuneValues.push([valueToUndo[0] + '.flag', new DT.TuneFlag(DT.ETuneFlag.USER)]);
    });
    return this.setValuesAsync(tuneValues);
  }

  public resetTuneValuesAsync(names: Array<string>, isDefault = true): Promise<void> {
    const values = names.map(
      (name: string): NamedValue => [
        name + '.flag',
        new DT.TuneFlag(
          isDefault ? (this._approvedNames.has(name) ? DT.ETuneFlag.APPROVED : DT.ETuneFlag.DEFAULT) : DT.ETuneFlag.USER
        )
      ]
    );
    return this._synchronizationContext.runAsyncTask(() => this._dataFlow.setValuesAsync(values));
  }

  public approveTuneValuesDefaults(): Promise<void> {
    return this._synchronizationContext.runAsyncTask(async () => {
      const newValues: ValuesCollection = [];
      const inputs = this._dataFlow.getInputs();
      this._approvedNames = new Set();
      for (const name of inputs.filter((name) => name.endsWith('.flag'))) {
        const flag = this._dataFlow.getValue(name, DT.TuneFlag);
        if (flag?.value === DT.ETuneFlag.USER) {
          const tuneName = name.substring(0, name.length - 5);
          const apprName = tuneName + '.appr';
          const userName = tuneName + '.user';
          const userValue = this._dataFlow.getIValue(userName);
          if (!userValue) {
            throw new Error('User value expected to be valid');
          }
          newValues.push([apprName, userValue], [name, new DT.TuneFlag(DT.ETuneFlag.APPROVED)]);
          this._approvedNames.add(tuneName);
        }
      }
      await this._dataFlow.setValuesAsync(newValues);
    });
  }

  public getApprovedNames(): Array<string> {
    return Array.from(this._approvedNames);
  }

  private _addTuneFlags(values: ValuesCollection): ValuesCollection {
    if (values.length == 0) {
      return values;
    }
    const tuneFlagsResetValues: ValuesCollection = [];
    const tuneNames = values.map((nv) => nv[0]);
    for (const name of this._dataFlow.getOutputsOf(tuneNames).filter((name) => name.endsWith('.dflt'))) {
      const flagName = name.substring(0, name.length - 5) + '.flag';
      if (!tuneNames.includes(flagName)) {
        const flagValue = this.getValue(flagName, DT.TuneFlag)?.value;
        if (flagValue !== DT.ETuneFlag.DEFAULT) {
          tuneFlagsResetValues.push([flagName, new DT.TuneFlag(DT.ETuneFlag.DEFAULT)]);
        }
      }
    }
    if (tuneFlagsResetValues.length == 0) {
      return values;
    }
    for (const [flagName] of tuneFlagsResetValues) {
      const name = flagName.substring(0, flagName.length - 5);
      this._notificationsCenter.onInfo('[' + name + '] Tune has been reset');
    }
    return values.concat(tuneFlagsResetValues);
  }

  public getIValueAsync(name: string): Promise<IValue | undefined> {
    // Safe as DataFlow could be in process of async operation (setValues is asynchronous)
    return this._synchronizationContext.runTask(() => this._dataFlow.getIValue(name));
  }

  public getValueAsync<T extends IValue>(name: string, constructor: ValueConstructor<T>): Promise<T | undefined> {
    // Safe as DataFlow could be in process of async operation (setValues is asynchronous)
    return this._synchronizationContext.runTask(() => this._dataFlow.getValue<T>(name, constructor));
  }

  public getValueAsAsync<T, U extends IValue & T = IValue & T>(
    name: string,
    constructor: ValueConstructor<U>
  ): Promise<T | undefined> {
    // Safe as DataFlow could be in process of async operation (setValues is asynchronous)
    return this._synchronizationContext.runTask(() => this._dataFlow.getValue<U>(name, constructor));
  }

  public async getValueStrictAsync<T extends IValue>(name: string, constructor: ValueConstructor<T>): Promise<T> {
    // Safe as DataFlow could be in process of async operation (setValues is asynchronous)
    const value = await this._synchronizationContext.runTask(() => this._dataFlow.getValue<T>(name, constructor));
    if (value === undefined) {
      throw new Error('Value must be initialized here: ' + name);
    }
    return value;
  }

  public getIValue(name: string): IValue | undefined {
    // Unsafe as DataFlow could be in process of async operation (setValues is asynchronous)
    return this._dataFlow.getIValue(name);
  }

  public getValue<T extends IValue>(name: string, constructor: ValueConstructor<T>): T | undefined {
    // Unsafe as DataFlow could be in process of async operation (setValues is asynchronous)
    return this._dataFlow.getValue<T>(name, constructor);
  }

  public getValueStrict<T extends IValue>(name: string, constructor: ValueConstructor<T>): T {
    // Unsafe as DataFlow could be in process of async operation (setValues is asynchronous)
    const value = this.getValue<T>(name, constructor);
    if (value === undefined) {
      throw new Error('Value must be initialized here: ' + name);
    }
    return value;
  }

  public getType(name: string): Type {
    // Unsafe as DataFlow could be in process of extension (extendAlgorithm is asynchronous)
    return this._dataFlow.getType(name);
  }

  public has(name: string): boolean {
    // Unsafe as DataFlow could be in process of extension (extendAlgorithm is asynchronous)
    return this._dataFlow.has(name);
  }

  public addOnChangedCallbackFor<T extends IValue>(name: string, func: OnChangedForCallbackT<T>): CallbackSubscription {
    // Unsafe as DataFlow could be in process of extension (extendAlgorithm is asynchronous)
    return this._dataFlow.addOnChangedCallbackFor<T>(name, func);
  }

  public addOnChangedCallback<T extends IValue>(func: OnChangedCallbackT<T>): CallbackSubscription {
    // Unsafe as DataFlow could be in process of extension (extendAlgorithm is asynchronous)
    return this._dataFlow.addOnChangedCallback<T>(func);
  }

  public addOnFailedCallback(func: OnFailedCallback): CallbackSubscription {
    // Unsafe as DataFlow could be in process of extension (extendAlgorithm is asynchronous)
    return this._dataFlow.addOnFailedCallback(func);
  }

  public getIdentifiersListTuner(): IdentifiersListTuner {
    return new IdentifiersListTuner(this);
  }

  public getNumberTuner(): NumberTuner {
    return new NumberTuner(this);
  }

  public getBooleanTuner(): BooleanTuner {
    return new BooleanTuner(this);
  }

  public snapshot(): Promise<void> {
    return this._synchronizationContext.runAsyncTask(() => new DFVDExporter(this, this._dataFlow.getAll()).download());
  }

  public export(): Promise<void> {
    return this._synchronizationContext.runAsyncTask(() => new MCSDExporter(this, this._dataFlow.getAll()).download());
  }

  public getDependentInputValues(names: Array<string>, allowNotInitialized = false): ValuesCollection {
    // Unsafe as DataFlow could be in process of async operation (setValues is asynchronous)
    // making it safe is not an option as it is being used during phases switch - so safe = deadlock
    const dependencies = this._dataFlow.getOutputsOf(names);
    const retNames: Array<string> = [];
    for (let i = 0, cnt = dependencies.length; i < cnt; ++i) {
      const name = dependencies[i];
      if (name.endsWith('.dflt')) {
        const baseName = name.substring(0, name.length - 5);
        retNames.push(baseName + '.flag');
        retNames.push(baseName + '.user');
        retNames.push(baseName + '.appr');
      }
    }
    retNames.push(...names);
    const ret: ValuesCollection = [];
    for (const name of retNames) {
      const value = this._dataFlow.getIValue(name);
      if (value) {
        ret.push([name, value]);
      } else {
        if (!allowNotInitialized) {
          throw new Error('Inputs expected to be initialized');
        }
      }
    }
    return ret;
  }

  public getDependentInputs(names: Array<string>): Array<string> {
    // Unsafe as DataFlow could be in process of extension (extendAlgorithm is asynchronous)
    return this._dataFlow.getInputsFor(names);
  }

  public getAncestorsOf(name: string, calculatorNames: Set<string>): Map<number, AncestryInfo> {
    return this._dataFlow.getAncestorsOf(name, calculatorNames);
  }

  public saveState(): Promise<ICaseState> {
    return this._synchronizationContext.runTask(() => {
      const state: ICaseState<StateMetadata> = {
        version: '2.1',
        metadata: {
          dataFlow: this._dataFlow.exportVertexInfo(new DataFlowJsonIO()),
          dataFlowExtensions: this._dataFlow.exportExtensionInfo(),
          approvedNames: Array.from(this._approvedNames)
        }
      };
      return state;
    });
  }

  public loadState(metadata: unknown): Promise<void> {
    return this._synchronizationContext.runAsyncTask(async () => {
      const meta = metadata as StateMetadata;
      await this._dataFlow.importVertexInfo(new DataFlowJsonIO(), meta.dataFlow);
      this._dataFlow.importExtensionInfo(meta.dataFlowExtensions);
      this._approvedNames = new Set(meta.approvedNames);
    });
  }
}
